import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { API_BASE_URL, createApiConfig } from '../../config/api';

const Sidebar = ({ tables, selectedTable, setSelectedTable, user, isOpen, onClose }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [visibleTables, setVisibleTables] = useState(tables);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch sidebar permissions and filter tables
  useEffect(() => {
    const fetchSidebarPermissions = async () => {
      try {
        // Try to get user-specific permissions first, fallback to role-based
        let response;
        if (user && user.user_id) {
          try {
            // Handle case mismatch for role name
            let roleName = user.role_name;
            if (roleName === 'mmda') {
              roleName = 'MMDA';
            }
            response = await axios.get(`${API_BASE_URL}/sidebar-permissions/user/${user.user_id}/visible?roleName=${roleName}`, createApiConfig());
          } catch (err) {
            // Fallback to role-based permissions if user-specific fail
            response = await axios.get(`${API_BASE_URL}/sidebar-permissions/role/MMDA/visible`, createApiConfig());
          }
        } else {
          response = await axios.get(`${API_BASE_URL}/sidebar-permissions/role/MMDA/visible`, createApiConfig());
        }
        const permissions = response.data;

        // Create a map of visible menu items
        const visibleMenuKeys = new Set(permissions.map(p => p.menu_item_key));

        // Filter tables based on permissions
        const filteredTables = tables.filter(table => {
          // Map table names to permission keys
          const tableKey = getTablePermissionKey(table.name);
          return visibleMenuKeys.has(tableKey);
        }).map(table => {
          // Filter subItems if they exist
          if (table.subItems) {
            const filteredSubItems = table.subItems.filter(subItem => {
              const subItemKey = getTablePermissionKey(subItem.name);
              return visibleMenuKeys.has(subItemKey);
            });
            return { ...table, subItems: filteredSubItems };
          }
          return table;
        });

        setVisibleTables(filteredTables);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch sidebar permissions:', error);
        // Fallback to showing all tables if permissions fetch fails
        setVisibleTables(tables);
        setPermissionsLoaded(true);
      }
    };

    if (tables && tables.length > 0 && user) {
      fetchSidebarPermissions();
    }
  }, [tables, user]);

  // Map table names to permission keys for MMDA
  const getTablePermissionKey = (tableName) => {
    const keyMap = {
      'Dashboard': 'dashboard',
      'Zone Management': 'zone_management',
      'My Zones': 'my_zones',
      'Groups': 'groups',
      'Revenue Officers': 'revenue_officers',
      'Traders': 'traders',
      'Users': 'users',
      'Devices': 'devices',
      'Payment Methods': 'payment_methods',
      'Receipts': 'receipts',
      'Transactions': 'transactions',
      'Audit Logs': 'audit_logs',
      'Support': 'support'
    };
    return keyMap[tableName] || tableName.toLowerCase().replace(/\s+/g, '_');
  };

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const handleItemClick = (table) => {
    if (table.subItems) {
      toggleExpanded(table.name);
    } else {
      setSelectedTable(table);
    }
  };

  const handleSubItemClick = (subItem, parentName) => {
    setSelectedTable({ ...subItem, parentName });
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
        />
      )}

      <div style={{
        width: 280,
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '20px 0',
        overflowY: 'auto',
        boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
        minWidth: 280,
        position: isMobile ? 'fixed' : 'relative',
        left: isMobile ? (isOpen ? 0 : '-280px') : 'auto',
        top: isMobile ? 0 : 'auto',
        height: isMobile ? '100vh' : 'auto',
        zIndex: 1000,
        transition: 'left 0.3s ease'
      }}>
      {/* MMDA Admin Header */}
      <div style={{
        padding: '0 24px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.2)',
        marginBottom: 20
      }}>
        <h3 style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 'bold',
          color: '#fff',
          letterSpacing: '0.5px'
        }}>
          MMDA Admin
        </h3>
        <p style={{
          margin: '8px 0 0',
          fontSize: 13,
          opacity: 0.9,
          color: '#e3f2fd',
          lineHeight: 1.4
        }}>
          {user.mmda_name || 'Regional Dashboard'}
        </p>
      </div>

      {/* Navigation Items */}
      <nav>
        {!permissionsLoaded ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            opacity: 0.8,
            fontSize: '14px'
          }}>
            Loading menu...
          </div>
        ) : (
          visibleTables.map((table, index) => (
          <div key={index}>
            <div
              onClick={() => handleItemClick(table)}
              style={{
                padding: '14px 24px',
                cursor: 'pointer',
                backgroundColor: selectedTable.name === table.name ? 'rgba(255,255,255,0.1)' : 'transparent',
                borderLeft: selectedTable.name === table.name ? '4px solid #fff' : '4px solid transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                fontSize: 14,
                fontWeight: selectedTable.name === table.name ? 'bold' : 'normal'
              }}
              onMouseEnter={(e) => {
                if (selectedTable.name !== table.name) {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTable.name !== table.name) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {table.icon}
                <span>{table.name}</span>
              </div>
              {table.subItems && (
                expandedItems[table.name] ? 
                <MdExpandLess size={20} /> : 
                <MdExpandMore size={20} />
              )}
            </div>

            {/* Sub-items */}
            {table.subItems && expandedItems[table.name] && (
              <div style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
                {table.subItems.map((subItem, subIndex) => (
                  <div
                    key={subIndex}
                    onClick={() => handleSubItemClick(subItem, table.name)}
                    style={{
                      padding: '12px 24px 12px 56px',
                      cursor: 'pointer',
                      backgroundColor: selectedTable.name === subItem.name ? 'rgba(255,255,255,0.1)' : 'transparent',
                      borderLeft: selectedTable.name === subItem.name ? '4px solid #fff' : '4px solid transparent',
                      fontSize: 13,
                      fontWeight: selectedTable.name === subItem.name ? 'bold' : 'normal',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTable.name !== subItem.name) {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTable.name !== subItem.name) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {subItem.icon && <span style={{ opacity: 0.8 }}>{subItem.icon}</span>}
                    <span>{subItem.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          ))
        )}
      </nav>

      {/* User Info */}
      <div style={{ 
        position: 'absolute', 
        bottom: 20, 
        left: 20, 
        right: 20,
        padding: 15,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 8,
        fontSize: 12
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
          {user.full_name}
        </div>
        <div style={{ opacity: 0.8 }}>
          {user.role_name?.replace(/_/g, ' ') || 'MMDA Admin'}
        </div>
        <div style={{ opacity: 0.6, marginTop: 5 }}>
          {user.email}
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
